import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Eye, Scale } from "lucide-react";

export default function AdminLegal() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  const load = () => api.get("/admin/legal").then((r) => { setItems(r.data); if (!active) setActive(r.data[0]); }).catch(() => toast.error("Erreur"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="admin-legal">
      <h1 className="display text-2xl sm:text-3xl text-[#0A1128] font-extrabold">Textes légaux</h1>
      <p className="text-sm text-[#64748B] mt-1">Pages publiques éditables : CGV, mentions légales, politique de confidentialité.</p>

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1 bg-white border border-black/8 rounded-2xl p-3">
          {items.map((d) => (
            <button key={d.slug} onClick={() => setActive(d)} data-testid={`legal-tab-${d.slug}`} className={`w-full text-left p-3 rounded-lg mb-1 transition-colors flex items-center gap-2 ${active?.slug === d.slug ? "bg-[#06B6D4]/10 text-[#0A1128]" : "hover:bg-[#FAFAFA]"}`}>
              <Scale size={14} className="text-[#06B6D4]" />
              <div className="flex-1 min-w-0">
                <div className="display font-bold text-sm truncate">{d.title}</div>
                <div className="text-[10px] text-[#64748B] truncate">/{d.slug}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {active && <LegalEditor key={active.slug} initial={active} onSaved={() => load()} />}
        </div>
      </div>
    </div>
  );
}

function LegalEditor({ initial, onSaved }) {
  const [form, setForm] = useState({ title: initial.title, content_html: initial.content_html });
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(() => /<\/?(p|h\d|ul|ol|li|strong|em|a|br)\b/i.test(initial.content_html || "") ? "html" : "plain");

  // Convert plain text -> HTML: each non-empty line becomes a <p>, lines starting with "# " or "## " become headings
  const plainToHtml = (txt) => {
    if (!txt) return "";
    const lines = txt.split(/\r?\n/);
    const out = [];
    let para = [];
    const flush = () => { if (para.length) { out.push(`<p>${para.join("<br/>")}</p>`); para = []; } };
    for (const raw of lines) {
      const line = raw.trim();
      if (!line) { flush(); continue; }
      const h2 = line.match(/^##\s+(.+)$/);
      const h3 = line.match(/^###\s+(.+)$/);
      if (h2) { flush(); out.push(`<h2>${h2[1]}</h2>`); continue; }
      if (h3) { flush(); out.push(`<h3>${h3[1]}</h3>`); continue; }
      para.push(line);
    }
    flush();
    return out.join("");
  };

  // Convert HTML -> plain text for editing (simple, lossy but fine for our purpose)
  const htmlToPlain = (html) => {
    if (!html) return "";
    return html
      .replace(/<h2>(.*?)<\/h2>/gi, "\n## $1\n")
      .replace(/<h3>(.*?)<\/h3>/gi, "\n### $1\n")
      .replace(/<p>(.*?)<\/p>/gi, "$1\n\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/?(strong|b)>/gi, "**")
      .replace(/<\/?(em|i)>/gi, "*")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  const [plainDraft, setPlainDraft] = useState(() => htmlToPlain(initial.content_html || ""));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        title: form.title,
        content_html: mode === "plain" ? plainToHtml(plainDraft) : form.content_html,
      };
      await api.put(`/admin/legal/${initial.slug}`, payload);
      toast.success("Texte enregistré");
      onSaved();
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  const switchMode = (next) => {
    if (next === "plain") {
      setPlainDraft(htmlToPlain(form.content_html));
    } else {
      setForm((f) => ({ ...f, content_html: plainToHtml(plainDraft) }));
    }
    setMode(next);
  };

  const previewHtml = mode === "plain" ? plainToHtml(plainDraft) : form.content_html;

  return (
    <div className="bg-white border border-black/8 rounded-2xl p-5 space-y-4" data-testid="legal-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-[#64748B]">Slug : <code className="bg-[#FAFAFA] px-1.5 py-0.5 rounded">/{initial.slug}</code>{initial.updated_at && <> · MAJ : {initial.updated_at.slice(0, 10)}</>}</div>
        <div className="flex gap-2">
          <a href={`/${initial.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#06B6D4] hover:underline font-bold"><Eye size={12} />Voir page publique</a>
          <Button onClick={save} disabled={busy} data-testid="legal-save" className="bg-[#0A1128] text-white"><Save size={14} className="mr-1" />{busy ? "..." : "Enregistrer"}</Button>
        </div>
      </div>

      <div><Label>Titre</Label><Input data-testid="legal-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>

      <div className="inline-flex items-center gap-1 bg-[#FAFAFA] border border-black/10 rounded-lg p-1 text-xs" data-testid="legal-mode-switch">
        <button type="button" onClick={() => switchMode("plain")} data-testid="legal-mode-plain" className={`px-3 py-1.5 rounded-md font-bold transition-colors ${mode === "plain" ? "bg-[#0A1128] text-white" : "text-[#64748B]"}`}>Texte simple</button>
        <button type="button" onClick={() => switchMode("html")} data-testid="legal-mode-html" className={`px-3 py-1.5 rounded-md font-bold transition-colors ${mode === "html" ? "bg-[#0A1128] text-white" : "text-[#64748B]"}`}>HTML avancé</button>
      </div>

      {mode === "plain" ? (
        <div>
          <Label>Contenu <span className="text-xs text-[#64748B]">— écrivez normalement. Préfixez par <code className="bg-[#FAFAFA] px-1 rounded">## </code> pour un titre. Une ligne vide = nouveau paragraphe.</span></Label>
          <Textarea
            data-testid="legal-content-plain"
            rows={20}
            value={plainDraft}
            onChange={(e) => setPlainDraft(e.target.value)}
            placeholder="## Mon titre&#10;&#10;Mon premier paragraphe.&#10;&#10;Mon second paragraphe avec **du gras** au besoin."
            className="text-sm"
          />
        </div>
      ) : (
        <div>
          <Label>Contenu HTML <span className="text-xs text-[#64748B]">(balises supportées : h2, p, ul, li, strong, a, etc.)</span></Label>
          <Textarea data-testid="legal-content" rows={20} value={form.content_html || ""} onChange={(e) => setForm({ ...form, content_html: e.target.value })} className="font-mono text-xs" />
        </div>
      )}

      <div className="bg-[#FAFAFA] rounded-xl border border-black/8 p-4">
        <div className="text-xs font-bold tracking-widest uppercase text-[#06B6D4] mb-2">Aperçu</div>
        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml || "<p><em>Aperçu indisponible</em></p>" }} />
      </div>
    </div>
  );
}
